import React from "react";
import YearAmount from "../../basics/YearAmount";
import FeeDetail from "./FeeDetail";
import Apply from "./Apply";
import ClosingHeader from "./ClosingHeader";
import TotalClosingCosts from "./TotalClosingCosts";
import { AutomationPlacement } from "../../../../utilities/automation"
import useLanguage from "../../../../utilities/useLanguage";
import { getTranslation } from "../../translations";
import { FeeType } from "../../common/FeeType";
import { IEmailForm, IEmployee } from "../../models";
import { IMortageQuote } from "../../models/IMortageQuote";

export interface IFeeProps {
    details: IMortageQuote,
    loanName: string,
    loanLabel?: string,
    emailValues: IEmailForm,
    employee?: IEmployee,
    show: boolean,
    taxes?: number,
    rate?: any
    insurance?: number,
    sendEmail: (email: IEmailForm) => void,
    referrerId?: string;
}

export default (props: IFeeProps) => {
    const { details, taxes, insurance, employee, show, loanName, loanLabel, rate, referrerId } = props;
    const { lang } = useLanguage();

    function getMonthlyTaxAndInsurance() {
        let monthlyPayment = details.Payments;
        let monthlyInsurance = 0;
        let monthlyTaxes = 0
        if (taxes > 0) {
            monthlyTaxes = taxes / 12;
            monthlyPayment += monthlyTaxes;
        }
        if (insurance > 0) {
            monthlyInsurance = insurance / 12;
            monthlyPayment += monthlyInsurance;
        }
        return {
            monthlyPayment,
            monthlyInsurance,
            monthlyTaxes,
        };
    }

    if (show && details) {
        const feeMap = details.FeeMap;
        const { monthlyInsurance, monthlyPayment, monthlyTaxes } = getMonthlyTaxAndInsurance();

        const closingHeaderProps = {
            className: "col-12 px-3 pt-3 pb-0",
            subHeaderClassName: "small",
            headerText: loanName,
            label: loanLabel
        };

        const yearAmountProps = {
            monthlyPayment: monthlyPayment,
            initialRate: rate.rate.initialRate,
            apr: rate.rate.disclosureInformation.apr,
            className: "text-primary h-90 pt-4",
            percentageClassName: "text-muted mt-3",
            perMonthClassName: "",
            amountLargeFontSize: 42,
            percentFontSize: 18,
            location: AutomationPlacement.MODAL
        };

        const constructionInspectionFees = (feeMap[FeeType.INSPECTION] || 0) + (feeMap[FeeType.FINAL_REINSPECTION] || 0);

        return (
            <div className="m-2 px-4">
                <ClosingHeader {...closingHeaderProps} />
                <div className="row">
                    <div className="d-none d-md-block col-lg-4 col-md-6 col-12 pt-0 pb-0 border-md-border-0 border-bottom-0 border-lg-right">
                        <YearAmount {...yearAmountProps} />
                    </div>
                    <div className="col-lg-4 col-md-6 col-12 pl-3 pt-3 pb-0  border-md-border-0 border-bottom-0 border-lg-right">
                        <FeeDetail label={getTranslation(lang).principalAndInterest} feeClassName="p-0 font-weight-bold text-right" labelClassName="font-weight-bold" fee={details.Payments} />
                        <FeeDetail label={getTranslation(lang).taxes} feeClassName="p-0 font-weight-bold text-right" labelClassName="font-weight-bold" fee={monthlyTaxes} />
                        <FeeDetail label={getTranslation(lang).insurance} feeClassName="p-0 font-weight-bold text-right" labelClassName="font-weight-bold" fee={monthlyInsurance} />
                    </div>
                    <Apply disabled={false} referrerId={referrerId} />
                </div>
                <TotalClosingCosts totalClosingCost={details.TotalClosingCost} />
                <div className="row">
                    <div className="col-lg-4 col-md-6 col-12 px-3 pt-3 pb-0 border-lg-right">
                        <FeeDetail feeClassName="text-right m-0 p-0" labelClassName="font-weight-bold" label={getTranslation(lang).lenderFees} fee={""} />
                        <FeeDetail feeClassName="p-0 text-right" label={getTranslation(lang).loanOriginationFee} fee={feeMap[FeeType.LOAN_ORIGINATION] || 0} />
                        <FeeDetail feeClassName="p-0 text-right" label={getTranslation(lang).processingFee} fee={feeMap[FeeType.PROCESSING] || 0} />
                        <FeeDetail feeClassName="p-0 text-right" label={getTranslation(lang).mortgagePoints} fee={feeMap[FeeType.POINTS] || 0} />
                        <FeeDetail feeClassName="text-right font-weight-bold border border-dark border-left-0 border-right-0 border-bottom-0 p-0 pt-1 mt-2" label="" fee={feeMap[FeeType.TOTAL_LENDER_FEES] || 0} />
                    </div>
                    <div className="col-lg-4 col-md-6 col-12 px-3 pt-3 pb-0 border-lg-right">
                        <FeeDetail feeClassName="text-right m-0 p-0" labelClassName="font-weight-bold" label={getTranslation(lang).thirdPartyCosts} fee={""} />
                        <FeeDetail feeClassName="p-0 text-right" label={getTranslation(lang).creditReportFee} fee={feeMap[FeeType.CREDIT_REPORT] || 0} />
                        <FeeDetail feeClassName="p-0 text-right" label={getTranslation(lang).titleFees} fee={feeMap[FeeType.TITLE_FEES] || 0} />
                        <FeeDetail feeClassName="p-0 text-right" label={getTranslation(lang).appraisal} fee={feeMap[FeeType.APPRAISAL] || 0} />
                        {feeMap[FeeType.INSPECTION] !== undefined || feeMap[FeeType.FINAL_REINSPECTION] !== undefined ? (
                            <FeeDetail feeClassName="p-0 text-right" label={getTranslation(lang).constructionInspectionFees} fee={constructionInspectionFees} />
                        ): null}
                        <FeeDetail feeClassName="text-right font-weight-bold border border-dark border-left-0 border-right-0 border-bottom-0 p-0 pt-1 mt-2" label="" fee={feeMap[FeeType.TOTAL_THIRD_PARTY] || 0} />
                    </div>
                    <div className="col-lg-4 col-md-6 col-12 px-3 pt-3 pb-0">
                        <FeeDetail feeClassName="text-right m-0 p-0" labelClassName="font-weight-bold" label={getTranslation(lang).itemsDueAtClosing} fee={""} />
                        <FeeDetail feeClassName="text-right m-0 p-0" label={getTranslation(lang).interestPartialMonth} fee={"TBD"} />
                        <FeeDetail feeClassName="text-right m-0 p-0" label={getTranslation(lang).homeOwnersInsurance} fee={"TBD"} />
                        <FeeDetail feeClassName="text-right m-0 p-0" label={getTranslation(lang).propertyTaxes} fee={"TBD"} />
                        <FeeDetail feeClassName="text-right font-weight-bold border border-dark border-left-0 border-right-0 border-bottom-0 p-0 pt-1 mt-2" label="" fee={"TBD"} />
                    </div>
                    <div className="col-lg-4 col-md-6 col-12 px-0 pt-3 pb-0">
                        <div className={"d-xs-flex d-md-none"}>
                            <FeeDetail feeClassName="text-right m-0 p-0" label={getTranslation(lang).totalEstClosingCosts} fee={details.TotalClosingCost || 0} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    return null;
};